import React, { useCallback, useEffect, useState } from "react"

import { useSetSimulationOutputPublicTransport } from "@/runs"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from "@coval/ui"
import { Download } from "lucide-react"

import { logger } from "@/lib/logger"
import { MakePublicConfirmDialog } from "@/components/shared/alerts/MakePublicConfirmDialog"
import {
  convertDrillDownDataToCSV,
  downloadCSV,
  type DrillDownColumnConfig,
} from "@/utils/csv/csv-export"
import { toISODateString } from "@/utils/date/date-range-helpers"
import {
  generateInternalLinks,
  generateShareableLinks,
} from "@/utils/shareable-links"

import type { DrillDownRunRecord } from "../widgets/types/core"

const log = logger("DrillDownCSVExport")

interface DrillDownCSVExportDropdownProps {
  rows: DrillDownRunRecord[]
  columns: DrillDownColumnConfig[]
  formatTimestamp: (timestamp: string) => string
  additionalValuesMap?: Map<string, Map<string, string | number>>
  metricDisplayLabel?: string
  slug: string
}

export const DrillDownCSVExportDropdown: React.FC<
  DrillDownCSVExportDropdownProps
> = ({
  rows,
  columns,
  formatTimestamp,
  additionalValuesMap,
  metricDisplayLabel,
  slug,
}) => {
  const [isExporting, setIsExporting] = useState(false)
  // The shareable export publishes every simulation in the view, so it waits on
  // the same confirmation as the other make-public controls. It holds the rows
  // the dialog named: the parent passes a live collection that a filter or a
  // refresh can replace while the dialog is open, and the export must publish
  // what the user authorised rather than whatever arrived since.
  const [pendingRows, setPendingRows] = useState<DrillDownRunRecord[] | null>(
    null,
  )
  const rowsKey = (rows ?? []).map((row) => row.simulation_output_id).join(",")

  useEffect(() => {
    setPendingRows(null)
  }, [rowsKey])

  // Mutation for making simulations public
  const setSimulationOutputPublicMutation =
    useSetSimulationOutputPublicTransport()

  // Handle CSV export with link type
  const handleExportCSV = useCallback(
    async (
      linkType: "internal" | "shareable",
      rowsToExport: DrillDownRunRecord[] = rows,
    ) => {
      if (!rowsToExport || rowsToExport.length === 0) {
        return
      }

      setIsExporting(true)

      try {
        let linkMap: Map<string, string>

        if (linkType === "shareable") {
          // Make each simulation public first
          for (const row of rowsToExport) {
            try {
              await setSimulationOutputPublicMutation.mutateAsync({
                simulation_output_id: row.simulation_output_id,
                is_public: true,
              })
            } catch (error) {
              log.error("Failed to make simulation public", error, {
                simulationOutputId: row.simulation_output_id,
              })
              // Continue with other simulations even if one fails
            }
          }

          // Generate shareable links
          linkMap = generateShareableLinks(rowsToExport)
        } else {
          // Generate internal links
          linkMap = generateInternalLinks(rowsToExport, slug)
        }

        const csvContent = convertDrillDownDataToCSV(
          rowsToExport,
          columns,
          formatTimestamp,
          additionalValuesMap,
          linkMap,
        )

        if (!csvContent) {
          return
        }

        const timestamp = toISODateString()
        const metricName = metricDisplayLabel || "metric"
        const sanitizedMetricName = metricName
          .replace(/[^a-z0-9]/gi, "_")
          .toLowerCase()
          .substring(0, 30)
        const filename = `drilldown-${sanitizedMetricName}-${timestamp}.csv`

        downloadCSV(csvContent, filename)
      } catch (error) {
        log.error("Error exporting CSV", error, { linkType })
      } finally {
        setIsExporting(false)
      }
    },
    [
      rows,
      columns,
      formatTimestamp,
      additionalValuesMap,
      metricDisplayLabel,
      slug,
      setSimulationOutputPublicMutation,
    ],
  )

  const handleConfirmShareableExport = useCallback(async () => {
    if (!pendingRows) {
      return
    }
    await handleExportCSV("shareable", pendingRows)
    setPendingRows(null)
  }, [handleExportCSV, pendingRows])

  const isExportDisabled = !rows || rows.length === 0 || isExporting

  return (
    <>
      <DropdownMenu>
        <Tooltip content="Export to CSV" triggerDisabled={isExportDisabled}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              aria-label="Export to CSV"
              disabled={isExportDisabled}
            >
              <Download className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            disabled={isExporting}
            onSelect={() => void handleExportCSV("internal")}
          >
            <div className="flex flex-col">
              <span>Download with Internal Links</span>
              <span className="text-xs text-coval-text-muted">
                Links only accessible to organization members
              </span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={isExporting}
            onSelect={() => setPendingRows(rows)}
          >
            <div className="flex flex-col">
              <span>Download with Shareable Links</span>
              <span className="text-xs text-coval-text-muted">
                Creates shareable links for each simulation
              </span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <MakePublicConfirmDialog
        open={pendingRows !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingRows(null)
          }
        }}
        onConfirm={() => {
          void handleConfirmShareableExport()
        }}
        resource="simulation"
        count={pendingRows?.length ?? 0}
        detail="Every simulation in this view is published so its link works outside your organization."
        pending={isExporting}
      />
    </>
  )
}
